import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChevronLeft, 
  faVolumeHigh, 
  faBackwardStep, 
  faForwardStep, 
  faPause, 
  faPlay,
  faLightbulb, 
  faCircleCheck 
} from '@fortawesome/free-solid-svg-icons';
import { createSpeechService } from '../utils/speechService'; // 导入公共语音服务


const DictationPage = ({ 
  words = [], 
  bookInfo = { grade: '三年级', term: '上册', unit: 'Unit 2' },
  settings = { 
    repeatCount: 3, 
    repeatInterval: 2, 
    wordInterval: 5,
    voiceType: 'system', // 语音类型：system, api, local
    voiceOptions: {
      lang: 'en-US',
      rate: 0.8,
      pitch: 1,
      volume: 1
    }
  },
  onComplete
}) => {
  const navigate = useNavigate();
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [repetitionCount, setRepetitionCount] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState('');
  const [canViewAnswer, setCanViewAnswer] = useState(false);
  // 添加音频加载状态
  const [audioLoadingStatus, setAudioLoadingStatus] = useState({});
  
// 添加计时器引用
const timerRef = useRef(null);
// 添加语音服务引用
const speechServiceRef = useRef(null);
// 添加当前语音播放控制引用
const currentSpeechRef = useRef(null);
// 添加倒计时计时器引用
const countdownTimerRef = useRef(null);
// 添加总时间引用
const totalTimeRef = useRef(0);
// 添加预加载队列引用
const preloadQueueRef = useRef([]);
// 添加预加载状态引用
const preloadingRef = useRef(false);
  
  const totalWords = words.length || 20;
  const progress = ((currentWordIndex + 1) / totalWords) * 100;
  
  // 获取当前单词
  const currentWord = words[currentWordIndex] || { word: '' };
  
    // 计算总时间和剩余时间
    useEffect(() => {
      // 计算总时间（秒）
      // 每个单词播放次数 * 重复间隔 + 单词间隔 * (总单词数 - 1)
      const totalTimeInSeconds = 
        totalWords * settings.repeatCount * settings.repeatInterval + 
        settings.wordInterval * (totalWords - 1);
      
      totalTimeRef.current = totalTimeInSeconds;
      
      // 计算已经过去的时间
      const elapsedWords = currentWordIndex;
      const elapsedTimeInSeconds = 
        elapsedWords * (settings.repeatCount * settings.repeatInterval + settings.wordInterval) +
        (repetitionCount - 1) * settings.repeatInterval;
      
      // 计算剩余时间
      const remainingTimeInSeconds = Math.max(0, totalTimeInSeconds - elapsedTimeInSeconds);
      
      // 格式化为分:秒
      const minutes = Math.floor(remainingTimeInSeconds / 60);
      const seconds = Math.floor(remainingTimeInSeconds % 60);
      setTimeRemaining(`${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
      
      // 设置倒计时
      if (!isPaused) {
        // 清除之前的倒计时
        if (countdownTimerRef.current) {
          clearInterval(countdownTimerRef.current);
        }
        
        // 每秒更新一次剩余时间
        countdownTimerRef.current = setInterval(() => {
          setTimeRemaining(prev => {
            const [mins, secs] = prev.split(':').map(Number);
            let totalSeconds = mins * 60 + secs - 1;
            
            if (totalSeconds <= 0) {
              clearInterval(countdownTimerRef.current);
              return '00:00';
            }
            
            const newMinutes = Math.floor(totalSeconds / 60);
            const newSeconds = totalSeconds % 60;
            return `${newMinutes.toString().padStart(2, '0')}:${newSeconds.toString().padStart(2, '0')}`;
          });
        }, 1000);
      }
      
      return () => {
        if (countdownTimerRef.current) {
          clearInterval(countdownTimerRef.current);
        }
      };
    }, [currentWordIndex, repetitionCount, settings, totalWords, isPaused]);
    
  // 预加载单词音频的函数
  const preloadWordAudio = (wordIndex, priority = false) => {
    if (wordIndex < 0 || wordIndex >= words.length) return;
    
    const wordToPreload = words[wordIndex];
    if (!wordToPreload || !wordToPreload.word) return;
    
    // 检查是否已经在加载或已加载
    if (audioLoadingStatus[wordIndex] === 'loading' || audioLoadingStatus[wordIndex] === 'loaded') {
      return;
    }
    
    // 标记为正在加载
    setAudioLoadingStatus(prev => ({
      ...prev,
      [wordIndex]: 'loading'
    }));
    
    // 使用Set数据结构确保队列中不会有重复项
    const currentQueue = new Set(preloadQueueRef.current);
    
    // 如果单词已在队列中，则移除它
    if (currentQueue.has(wordIndex)) {
      currentQueue.delete(wordIndex);
    }
    
    // 如果是优先级高的（当前要播放的），直接加入队列前面
    if (priority) {
      preloadQueueRef.current = [wordIndex, ...Array.from(currentQueue)];
    } else {
      // 否则加入队列末尾
      currentQueue.add(wordIndex);
      preloadQueueRef.current = Array.from(currentQueue);
    }
    
    // 如果没有正在预加载的任务，启动预加载
    if (!preloadingRef.current) {
      processPreloadQueue();
    }
  };
  
  // 处理预加载队列
  const processPreloadQueue = async () => {
    // 如果队列为空，结束预加载
    if (preloadQueueRef.current.length === 0) {
      preloadingRef.current = false;
      return;
    }
    
    // 标记为正在预加载
    preloadingRef.current = true;
    
    // 取出队列中的第一个单词索引
    const wordIndex = preloadQueueRef.current.shift();
    const wordToPreload = words[wordIndex];
    
    if (!wordToPreload || !wordToPreload.word) {
      // 如果单词无效，继续处理下一个
      processPreloadQueue();
      return;
    }
    
    // 重试计数器
    let retryCount = 0;
    const maxRetries = 3;
    
    const attemptPreload = async () => {
      try {
        if (!speechServiceRef.current) {
          throw new Error('语音服务未初始化');
        }
        
        // 使用语音服务预加载单词
        const preloadResult = speechServiceRef.current.speak(
          wordToPreload.word,
          settings.voiceOptions,
          true // 静默模式，仅预加载不播放
        );
        
        // 如果有加载Promise（如Asugar API），等待加载完成
        if (preloadResult && preloadResult.loadingPromise) {
          await preloadResult.loadingPromise;
        }
        
        // 标记为已加载
        setAudioLoadingStatus(prev => ({
          ...prev,
          [wordIndex]: 'loaded'
        }));
        
        console.log(`单词预加载成功: ${wordToPreload.word}`);
        
        // 继续处理队列中的下一个单词
        setTimeout(processPreloadQueue, 100); // 添加小延迟防止过快加载导致的问题
      } catch (error) {
        console.error(`单词预加载失败: ${wordToPreload.word}，尝试次数: ${retryCount + 1}/${maxRetries}`, error);
        
        if (retryCount < maxRetries) {
          // 增加重试计数
          retryCount++;
          
          // 设置重试状态
          setAudioLoadingStatus(prev => ({
            ...prev,
            [wordIndex]: `retrying-${retryCount}`
          }));
          
          // 延迟一段时间后重试，使用指数退避策略
          const retryDelay = Math.min(1000 * Math.pow(2, retryCount - 1), 5000);
          console.log(`${retryDelay/1000}秒后重试加载单词: ${wordToPreload.word}`);
          setTimeout(attemptPreload, retryDelay);
        } else {
          // 超过最大重试次数，标记为加载失败
          console.error(`单词 ${wordToPreload.word} 预加载失败，已达到最大重试次数`);
          setAudioLoadingStatus(prev => ({
            ...prev,
            [wordIndex]: 'error'
          }));
          
          // 继续处理队列中的下一个单词
          setTimeout(processPreloadQueue, 100);
        }
      }
    };
    
    // 开始尝试预加载
    attemptPreload();
  };

  // 初始化语音服务
  useEffect(() => {
    // 创建一个引用来存储初始化函数
    let handleInitClickRef = null;
    
    try {
      // 创建主语音服务
      speechServiceRef.current = createSpeechService(settings.voiceType);
      
      // 检查语音服务是否可用
      if (!speechServiceRef.current.isSupported()) {
        console.warn('当前选择的语音服务不受支持，尝试使用系统语音作为备选');
        // 尝试使用系统语音作为备选
        speechServiceRef.current = createSpeechService('system');
        
        if (!speechServiceRef.current.isSupported()) {
          alert('当前浏览器不支持任何语音合成，请使用现代浏览器访问。');
          return;
        }
      }
      
      // 在iOS上，需要用户交互才能播放语音
      const initSpeechSystem = () => {
        if (window.speechSynthesis) {
          // 尝试获取语音列表，这会初始化语音合成系统
          window.speechSynthesis.getVoices();
          
          // 播放一个空白的语音，以初始化语音系统
          const utterance = new SpeechSynthesisUtterance('');
          utterance.volume = 0; // 设置音量为0，用户听不到
          window.speechSynthesis.speak(utterance);
        }
        
        // 初始化Audio元素（对于API和本地音频服务）
        if (typeof Audio !== 'undefined') {
          const silent = new Audio();
          // 为该音频添加空白源（有些浏览器需要有源才能初始化）
          silent.src = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA';
          silent.volume = 0;
          // 尝试加载和播放
          silent.load();
          silent.play().catch(() => {
            // 忽略错误，这只是为了初始化
          });
        }
      };
      
      // 添加一个一次性的点击事件监听器，用于初始化语音
      handleInitClickRef = () => {
        initSpeechSystem();
        document.removeEventListener('click', handleInitClickRef);
        
        // 初始化后开始预加载单词
        startPreloadingWords();
      };
      
      // 如果已经在选择页面初始化过，则不需要再次初始化
      if (!settings.speechInitialized) {
        document.addEventListener('click', handleInitClickRef);
      } else {
        // 如果已经初始化过，直接开始预加载单词
        startPreloadingWords();
      }
    } catch (error) {
      console.error('初始化语音服务时出错:', error);
      alert('语音服务初始化失败，请刷新页面重试');
    }
    
    return () => {
      // 组件卸载时取消所有语音播放
      if (currentSpeechRef.current) {
        try {
          currentSpeechRef.current.cancel();
        } catch (error) {
          console.error('取消语音播放时出错:', error);
        }
      }
      
      // 清理音频缓存
      if (speechServiceRef.current && speechServiceRef.current.clearCache) {
        try {
          speechServiceRef.current.clearCache();
        } catch (error) {
          console.error('清理音频缓存时出错:', error);
        }
      }
      
      // 移除事件监听器
      if (handleInitClickRef) {
        document.removeEventListener('click', handleInitClickRef);
      }
    };
  }, [settings.voiceType, settings.speechInitialized]);
  
  // 开始预加载单词
  const startPreloadingWords = () => {
    // 预加载当前单词和未来几个单词
    const preloadCount = Math.min(5, words.length - currentWordIndex);
    
    // 先预加载当前单词（高优先级）
    preloadWordAudio(currentWordIndex, true);
    
    // 然后预加载后面的几个单词
    for (let i = 1; i < preloadCount; i++) {
      preloadWordAudio(currentWordIndex + i);
    }
  };
  
  // 当单词索引变化时，更新预加载队列
  useEffect(() => {
    if (!speechServiceRef.current) return;
    
    // 预加载未来几个单词
    const preloadCount = Math.min(5, words.length - currentWordIndex);
    
    // 先确保当前单词已加载（高优先级）
    preloadWordAudio(currentWordIndex, true);
    
    // 然后预加载后面的几个单词
    for (let i = 1; i < preloadCount; i++) {
      preloadWordAudio(currentWordIndex + i);
    }
  }, [currentWordIndex, words]);
  
  // 播放当前单词
  const playCurrentWord = async () => {
    if (!speechServiceRef.current) return;
    
    // 取消之前的语音播放并确保完全停止
    if (currentSpeechRef.current) {
      currentSpeechRef.current.cancel();
      // 添加小延迟确保前一个播放完全取消
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    // 获取当前单词文本
    const wordText = currentWord.word || '';
    if (!wordText) return;
    
    // 检查当前单词是否已加载
    const wordStatus = audioLoadingStatus[currentWordIndex];
    
    // 创建播放完成的Promise
    return new Promise((resolve) => {
      // 如果单词尚未加载或加载失败，先尝试加载
      if (wordStatus !== 'loaded') {
        console.log(`当前单词 ${wordText} 尚未加载完成，状态: ${wordStatus || '未开始'}，开始加载...`);
        
        // 高优先级预加载当前单词
        preloadWordAudio(currentWordIndex, true);
        
        // 等待一小段时间，让预加载有机会完成
        // 这里不会无限等待，最多等待3秒
        let waitTime = 0;
        const maxWaitTime = 3000; // 最多等待3秒
        const checkInterval = 100; // 每100毫秒检查一次
        
        const checkLoading = () => {
          const currentStatus = audioLoadingStatus[currentWordIndex];
          
          if (currentStatus === 'loaded') {
            // 加载完成，播放单词
            console.log(`单词 ${wordText} 已加载完成，开始播放`);
            try {
              const speechObj = speechServiceRef.current.speak(
                wordText, 
                settings.voiceOptions
              );
              currentSpeechRef.current = speechObj;
              
              // 设置一个合理的播放时间后解析Promise
              setTimeout(resolve, Math.max(500, wordText.length * 150));
            } catch (error) {
              console.error(`播放单词 ${wordText} 时出错:`, error);
              // 发生错误时尝试一次
              setTimeout(() => {
                try {
                  const speechObj = speechServiceRef.current.speak(
                    wordText, 
                    settings.voiceOptions
                  );
                  currentSpeechRef.current = speechObj;
                } catch (retryError) {
                  console.error(`重试播放单词 ${wordText} 失败:`, retryError);
                }
                // 无论结果如何，继续执行
                setTimeout(resolve, 500);
              }, 300);
            }
          } else if (waitTime >= maxWaitTime || currentStatus === 'error') {
            // 超时或加载失败，仍然尝试播放
            console.warn(`单词 ${wordText} 加载超时或失败，尝试直接播放`);
            try {
              const speechObj = speechServiceRef.current.speak(
                wordText, 
                settings.voiceOptions
              );
              currentSpeechRef.current = speechObj;
            } catch (error) {
              console.error(`播放单词 ${wordText} 时出错:`, error);
              // 错误情况下尝试使用系统语音作为备选
              try {
                const backupService = createSpeechService('system');
                const speechObj = backupService.speak(
                  wordText, 
                  settings.voiceOptions
                );
                currentSpeechRef.current = speechObj;
              } catch (backupError) {
                console.error(`备选方式播放单词 ${wordText} 失败:`, backupError);
              }
            }
            // 无论结果如何，继续执行
            setTimeout(resolve, Math.max(500, wordText.length * 150));
          } else {
            // 继续等待
            waitTime += checkInterval;
            setTimeout(checkLoading, checkInterval);
          }
        };
        
        checkLoading();
      } else {
        // 如果已加载，直接播放
        console.log(`单词 ${wordText} 已加载，直接播放`);
        try {
          const speechObj = speechServiceRef.current.speak(
            wordText, 
            settings.voiceOptions
          );
          currentSpeechRef.current = speechObj;
          
          // 根据单词长度设置一个合理的播放时间
          setTimeout(resolve, Math.max(500, wordText.length * 150));
        } catch (error) {
          console.error(`播放已加载单词 ${wordText} 时出错:`, error);
          // 发生错误时尝试使用系统语音作为备选
          try {
            const backupService = createSpeechService('system');
            const speechObj = backupService.speak(
              wordText, 
              settings.voiceOptions
            );
            currentSpeechRef.current = speechObj;
          } catch (backupError) {
            console.error(`备选方式播放单词 ${wordText} 失败:`, backupError);
          }
          // 设置一个默认的等待时间
          setTimeout(resolve, 500);
        }
      }
    });
  };
  
  // 清理计时器的函数
  const clearTimers = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };
  
  // 模拟播放单词的效果 - 修复播放次数和自动播放下一个单词
  useEffect(() => {
    if (isPaused) return;
    
    clearTimers();
    
    // 播放当前单词（异步）
    const startPlayback = async () => {
      try {
        // 等待播放完成
        await playCurrentWord();
        
        // 设置下一次播放或下一个单词的定时器
        timerRef.current = setTimeout(() => {
          if (repetitionCount < settings.repeatCount) {
            // 还有重复次数，继续播放当前单词
            setRepetitionCount(prev => prev + 1);
          } else {
            // 当前单词播放完成
            // 设置延时，自动播放下一个单词
            timerRef.current = setTimeout(() => {
              if (currentWordIndex < totalWords - 1) {
                // 还有下一个单词
                setCurrentWordIndex(prev => prev + 1);
                setRepetitionCount(1);
              } else {
                // 所有单词听写完成
                setCanViewAnswer(true);
              }
            }, settings.wordInterval * 1000); // 单词间隔时间
          }
        }, settings.repeatInterval * 1000); // 重复间隔时间
      } catch (error) {
        console.error('播放单词过程中出错:', error);
        // 错误时，也要确保继续进行
        timerRef.current = setTimeout(() => {
          if (repetitionCount < settings.repeatCount) {
            // 还有重复次数，继续播放当前单词
            setRepetitionCount(prev => prev + 1);
          } else if (currentWordIndex < totalWords - 1) {
            // 还有下一个单词
            setCurrentWordIndex(prev => prev + 1);
            setRepetitionCount(1);
          } else {
            // 所有单词听写完成
            setCanViewAnswer(true);
          }
        }, settings.repeatInterval * 1000);
      }
    };
    
    startPlayback();
    
    return () => clearTimers();
  }, [repetitionCount, isPaused, currentWordIndex, settings, totalWords, words, onComplete, navigate, audioLoadingStatus]);

  // 组件卸载时清理计时器
  useEffect(() => {
    return () => {
      clearTimers();
      // 清理倒计时计时器
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
      
      // 清理音频缓存
      if (speechServiceRef.current && speechServiceRef.current.clearCache) {
        speechServiceRef.current.clearCache();
      }
    };
  }, []);
  
  const handlePauseResume = () => {
    if (isPaused) {
      // 恢复播放
      setIsPaused(false);
      
      // 恢复倒计时
      // 格式化为分:秒
      const remainingTimeInSeconds = Math.max(0, totalTimeRef.current);
      const minutes = Math.floor(remainingTimeInSeconds / 60);
      const seconds = Math.floor(remainingTimeInSeconds % 60);
      setTimeRemaining(`${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
      
      // 设置倒计时
      countdownTimerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          const [mins, secs] = prev.split(':').map(Number);
          let totalSeconds = mins * 60 + secs - 1;
          
          if (totalSeconds <= 0) {
            clearInterval(countdownTimerRef.current);
            return '00:00';
          }
          
          const newMinutes = Math.floor(totalSeconds / 60);
          const newSeconds = totalSeconds % 60;
          return `${newMinutes.toString().padStart(2, '0')}:${newSeconds.toString().padStart(2, '0')}`;
        });
      }, 1000);
    } else {
      // 暂停播放
      setIsPaused(true);
      
      // 取消当前语音播放
      if (currentSpeechRef.current) {
        try {
          currentSpeechRef.current.cancel();
        } catch (error) {
          console.error('取消语音播放时出错:', error);
        }
      }
      
      // 清理计时器
      clearTimers();
      
      // 暂停倒计时
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    }
  };
  
  const handlePrevWord = () => {
    // 先暂停当前播放
    setIsPaused(true);
    
    // 清理计时器
    clearTimers();
    
    // 取消当前语音播放
    if (currentSpeechRef.current) {
      try {
        currentSpeechRef.current.cancel();
      } catch (error) {
        console.error('取消语音播放时出错:', error);
      }
    }
    
    // 等待操作完成
    setTimeout(() => {
      if (currentWordIndex > 0) {
        // 切换到上一个单词
        setCurrentWordIndex(prev => prev - 1);
        setRepetitionCount(1);
        setCanViewAnswer(false);
        
        // 确保上一个单词已预加载
        if (audioLoadingStatus[currentWordIndex - 1] !== 'loaded') {
          preloadWordAudio(currentWordIndex - 1, true);
        }
        
        // 延迟一点时间再恢复播放，确保状态已经更新
        setTimeout(() => {
          setIsPaused(false);
        }, 300);
      }
    }, 100);
  };
  
  const handleNextWord = () => {
    // 先暂停当前播放
    setIsPaused(true);
    
    // 清理计时器
    clearTimers();
    
    // 取消当前语音播放
    if (currentSpeechRef.current) {
      try {
        currentSpeechRef.current.cancel();
      } catch (error) {
        console.error('取消语音播放时出错:', error);
      }
    }
    
    // 等待操作完成
    setTimeout(() => {
      if (currentWordIndex < totalWords - 1) {
        // 切换到下一个单词
        setCurrentWordIndex(prev => prev + 1);
        setRepetitionCount(1);
        setCanViewAnswer(false);
        
        // 确保下一个单词已预加载
        if (audioLoadingStatus[currentWordIndex + 1] !== 'loaded') {
          preloadWordAudio(currentWordIndex + 1, true);
        }
        
        // 预加载更远的单词
        const furtherWordIndex = currentWordIndex + 2;
        if (furtherWordIndex < words.length && audioLoadingStatus[furtherWordIndex] !== 'loaded') {
          preloadWordAudio(furtherWordIndex);
        }
        
        // 延迟一点时间再恢复播放，确保状态已经更新
        setTimeout(() => {
          setIsPaused(false);
        }, 300);
      } else {
        // 所有单词听写完成
        if (onComplete) {
          onComplete();
        } else {
          navigate('/check-result');
        }
      }
    }, 100);
  };
  
  const handleExit = () => {
    if (window.confirm('确定要退出听写吗？当前进度将不会保存。')) {
      // 取消当前语音播放
      if (currentSpeechRef.current) {
        currentSpeechRef.current.cancel();
      }
      navigate('/');
    }
  };
  
  const handleViewAnswer = () => {
    // 取消当前语音播放
    if (currentSpeechRef.current) {
      currentSpeechRef.current.cancel();
    }
    
    // 所有单词听写完成后，才能查看答案
    if (onComplete) {
      // 准备结果数据 - 这里我们不需要用户输入，因为用户在纸上写答案
      const results = words.map(word => ({
        ...word,
        isCorrect: false, // 由于没有用户输入，无法判断正确性
        userAnswer: '' // 没有用户输入
      }));
      onComplete(results);
    } else {
      navigate('/check-result');
    }
  };
  
  // 手动重播当前单词
  const handleReplayWord = async () => {
    // 先暂停当前播放
    setIsPaused(true);
    
    // 清理计时器
    clearTimers();
    
    // 取消当前语音播放
    if (currentSpeechRef.current) {
      try {
        currentSpeechRef.current.cancel();
      } catch (error) {
        console.error('取消语音播放时出错:', error);
      }
    }
    
    // 等待一小段时间确保播放已停止
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // 重置重复次数
    setRepetitionCount(1);
    
    // 播放当前单词
    await playCurrentWord();
    
    // 恢复自动播放
    setIsPaused(false);
  };

  return (
    <div className="bg-[#f9f9f9] min-h-screen">
      {/* Main Content */}
      <div className="p-5 pb-24">
        {/* Navigation Header */}
        <div className="flex justify-between items-center mb-6">
          <button className="text-blue-500 flex items-center opacity-50" disabled>
            <FontAwesomeIcon icon={faChevronLeft} className="mr-1" />
            <span>返回</span>
          </button>
          <h2 className="text-xl font-bold">正在听写</h2>
          <button className="text-red-500" onClick={handleExit}>
            退出
          </button>
        </div>

        {/* Dictation Progress */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-medium text-gray-600">
              {bookInfo.grade}{bookInfo.term} {bookInfo.unit}
            </h3>
            <div className="text-blue-500 font-medium">
              {currentWordIndex + 1}/{totalWords}
            </div>
          </div>
          <div className="bg-gray-200 rounded-full h-2 mb-2">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all duration-300" 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>第{currentWordIndex + 1}个单词</span>
            <span>剩余: {timeRemaining}</span>
          </div>
        </div>

        {/* Current Word Visual */}
        <div className="mb-10 text-center">
          <div className="bg-white rounded-2xl shadow-sm p-8 mb-4 flex flex-col items-center justify-center min-h-[15rem]">
            <div className={`w-20 h-20 rounded-full ${audioLoadingStatus[currentWordIndex] === 'loading' ? 'bg-yellow-100' : 'bg-blue-100'} flex items-center justify-center mb-5 ${audioLoadingStatus[currentWordIndex] === 'loading' ? 'animate-pulse' : 'animate-pulse'}`}>
              <FontAwesomeIcon icon={faVolumeHigh} className={`${audioLoadingStatus[currentWordIndex] === 'loading' ? 'text-yellow-500' : 'text-blue-500'} text-2xl`} />
            </div>
            
            <div className="text-gray-400 mb-3 text-sm">
              {audioLoadingStatus[currentWordIndex] === 'loading' ? 
                `正在加载第${currentWordIndex + 1}个单词...` : 
                `正在播放第${currentWordIndex + 1}个单词 ${currentWord.word}`
              }
            </div>
            
            {/* Repetition Indicator - 动态显示重复次数 */}
            <div className="flex space-x-3">
              {Array.from({ length: settings.repeatCount }).map((_, index) => (
                <div 
                  key={index}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index + 1 <= repetitionCount ? 'bg-blue-500' : 'bg-blue-200'
                  }`}
                ></div>
              ))}
            </div>
          </div>
          
          {/* Word Count - 动态显示总重复次数 */}
          <div className="text-center">
            <span className="text-xs bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-medium">
              第{repetitionCount}次播放，共{settings.repeatCount}次
            </span>
          </div>
        </div>

        {/* Control Buttons */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <button 
            className="bg-gray-100 rounded-xl py-4 flex flex-col items-center justify-center"
            onClick={handlePrevWord}
          >
            <FontAwesomeIcon icon={faBackwardStep} className="text-gray-600 mb-2" />
            <span className="text-xs text-gray-600">上一个</span>
          </button>
          <button 
            className="bg-white shadow-md rounded-xl py-4 flex flex-col items-center justify-center border border-gray-100"
            onClick={handlePauseResume}
          >
            <FontAwesomeIcon 
              icon={isPaused ? faPlay : faPause} 
              className="text-blue-500 mb-2 text-xl" 
            />
            <span className="text-xs font-medium">{isPaused ? '继续' : '暂停'}</span>
          </button>
          <button 
            className="bg-gray-100 rounded-xl py-4 flex flex-col items-center justify-center"
            onClick={handleNextWord}
          >
            <FontAwesomeIcon icon={faForwardStep} className="text-gray-600 mb-2" />
            <span className="text-xs text-gray-600">下一个</span>
          </button>
        </div>

        {/* Instructions - 更新提示信息 */}
        <div className="bg-yellow-50 border border-yellow-100 rounded-xl p-4">
          <h4 className="font-medium text-yellow-700 mb-2 flex items-center">
            <FontAwesomeIcon icon={faLightbulb} className="mr-2" />
            提示
          </h4>
          <ul className="text-sm text-yellow-700 space-y-2">
            <li className="flex items-start">
              <FontAwesomeIcon icon={faCircleCheck} className="mr-2 mt-1 text-yellow-500 text-xs" />
              <span>认真听取每个单词，注意拼写</span>
            </li>
            <li className="flex items-start">
              <FontAwesomeIcon icon={faCircleCheck} className="mr-2 mt-1 text-yellow-500 text-xs" />
              <span>每个单词将重复播放{settings.repeatCount}次</span>
            </li>
            <li className="flex items-start">
              <FontAwesomeIcon icon={faCircleCheck} className="mr-2 mt-1 text-yellow-500 text-xs" />
              <span>重复间隔时间为{settings.repeatInterval}秒</span>
            </li>
            <li className="flex items-start">
              <FontAwesomeIcon icon={faCircleCheck} className="mr-2 mt-1 text-yellow-500 text-xs" />
              <span>单词间隔时间为{settings.wordInterval}秒</span>
            </li>
            <li className="flex items-start">
              <FontAwesomeIcon icon={faCircleCheck} className="mr-2 mt-1 text-yellow-500 text-xs" />
              <span>听完后可点击"查看答案"检查拼写</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Bottom Control Panel */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
        <button 
          className={`w-full py-3 rounded-xl font-medium ${
            canViewAnswer 
              ? 'bg-blue-500 text-white' 
              : 'bg-gray-200 text-gray-700'
          }`}
          disabled={!canViewAnswer}
          onClick={handleViewAnswer}
        >
          查看所有答案
        </button>
      </div>
    </div>
  );
};

export default DictationPage;